import React, { useEffect, useState, useMemo } from 'react';
import {
  Plus, FileText, DollarSign, StopCircle, Clock,
  BellRing, LogOut, KeyRound, AlertTriangle, CheckCircle2,
  CalendarDays, ChevronDown, ChevronUp, Hourglass, Search, Trash2, Pencil,
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import ConfirmDialog from '../components/ConfirmDialog';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtMoney = (n) => `Rs. ${Number(n || 0).toLocaleString()}`;

const duration = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  const totalDays = Math.max(0, Math.floor((end - start) / 86400000));
  const years = Math.floor(totalDays / 365);
  const months = Math.floor((totalDays % 365) / 30);
  const days = totalDays % 30;
  const parts = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}m`);
  if (days > 0 || parts.length === 0) parts.push(`${days}d`);
  return parts.join(' ');
};

// Days until (positive = future, negative = overdue)
const daysUntil = (dateStr) => {
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
};

const ExitCountdown = ({ exitDate }) => {
  const d = daysUntil(exitDate);
  if (d < 0)
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
        <AlertTriangle size={11} /> {Math.abs(d)}d overdue
      </span>
    );
  if (d === 0)
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
        <AlertTriangle size={11} /> Exit today
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
      <Hourglass size={11} /> {d}d left
    </span>
  );
};

// ─── Tab button ───────────────────────────────────────────────────────────────
const Tab = ({ active, onClick, children, badge }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
      ${active ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
  >
    {children}
    {badge > 0 && (
      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? 'bg-brand-100 text-brand-700' : 'bg-gray-100 text-gray-600'}`}>
        {badge}
      </span>
    )}
  </button>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Tenancies() {
  const [tenancies, setTenancies] = useState([]);
  const [students, setStudents] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [tab, setTab] = useState('active'); // 'active' | 'pending' | 'ended'
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // tenancy object

  // Modals
  const [showForm, setShowForm]           = useState(false);
  const [showEdit, setShowEdit]           = useState(null);   // tenancy being edited
  const [showPayment, setShowPayment]     = useState(null);   // tenancy object
  const [showNotice, setShowNotice]       = useState(null);   // tenancy object
  const [showKeyReturn, setShowKeyReturn] = useState(null);   // tenancy object
  const [confirmExit, setConfirmExit]     = useState(null);   // tenancy object
  const [confirmEnd, setConfirmEnd]       = useState(null);   // legacy

  // Forms
  const [form, setForm]         = useState({ studentId: '', roomId: '', startDate: '', monthlyRent: '', keyMoney: '', keyMoneyMonths: 2 });
  const [editForm, setEditForm] = useState({ roomId: '', monthlyRent: '', keyMoney: '', keyMoneyMonths: 2, startDate: '' });
  const [payForm, setPayForm] = useState({ amount: '', type: 'RENT', month: '', notes: '' });
  const [noticeForm, setNoticeForm] = useState({ noticeDate: '', exitDate: '' });
  const [keyReturnForm, setKeyReturnForm] = useState({ returnDate: '', returnAmount: '', notes: '' });

  const load = () =>
    api.get('/tenancies').then(r => setTenancies(r.data)).catch(console.error);

  useEffect(() => {
    load();
    api.get('/students').then(r => setStudents(r.data));
    api.get('/rooms').then(r => setRooms(r.data));
  }, []);

  // ── Search filter ────────────────────────────────────────────────────────
  const matchesSearch = (t) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      `${t.student.firstName} ${t.student.lastName}`.toLowerCase().includes(q) ||
      t.student.registrationNo.toLowerCase().includes(q) ||
      t.room.roomNumber.toLowerCase().includes(q) ||
      (t.student.phone || '').includes(q)
    );
  };

  // ── Derived lists ────────────────────────────────────────────────────────
  const active = useMemo(() => tenancies.filter(t => t.status === 'ACTIVE' && matchesSearch(t)), [tenancies, search]);
  const pending = useMemo(() => tenancies.filter(t => t.status === 'NOTICE_GIVEN' && matchesSearch(t)), [tenancies, search]);
  const ended = useMemo(() => tenancies.filter(t => t.status === 'ENDED' && matchesSearch(t)), [tenancies, search]);

  // Alert: NOTICE_GIVEN tenancies past their exit date
  const overdueExits = useMemo(
    () => pending.filter(t => t.exitDate && daysUntil(t.exitDate) < 0),
    [pending]
  );

  // ── Actions ──────────────────────────────────────────────────────────────
  const submit = async e => {
    e.preventDefault();
    try {
      await api.post('/tenancies', form);
      toast.success('Tenancy created');
      setShowForm(false);
      setForm({ studentId: '', roomId: '', startDate: '', monthlyRent: '', keyMoney: '', keyMoneyMonths: 2 });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const addPayment = async e => {
    e.preventDefault();
    try {
      await api.post(`/tenancies/${showPayment.id}/payments`, payForm);
      toast.success('Payment recorded');
      setShowPayment(null);
      setPayForm({ amount: '', type: 'RENT', month: '', notes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const submitNotice = async e => {
    e.preventDefault();
    try {
      await api.put(`/tenancies/${showNotice.id}/notice`, noticeForm);
      toast.success('Exit notice recorded');
      setShowNotice(null);
      setNoticeForm({ noticeDate: '', exitDate: '' });
      setTab('pending');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const doProcessExit = async () => {
    try {
      await api.put(`/tenancies/${confirmExit.id}/process-exit`);
      toast.success(`Exit processed for ${confirmExit.student.firstName}`);
      setConfirmExit(null);
      setTab('ended');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); setConfirmExit(null); }
  };

  const submitKeyReturn = async e => {
    e.preventDefault();
    try {
      await api.put(`/tenancies/${showKeyReturn.id}/keymoney-return`, keyReturnForm);
      toast.success('Key money return recorded');
      setShowKeyReturn(null);
      setKeyReturnForm({ returnDate: '', returnAmount: '', notes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/tenancies/${confirmDelete.id}`);
      toast.success('Tenancy deleted');
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error');
      setConfirmDelete(null);
    }
  };

  // ── Open payment modal with smart defaults ──────────────────────────────
  const openPayment = t => {
    const currentMonth = new Date().toISOString().slice(0, 7); // "YYYY-MM"
    setPayForm({ amount: Number(t.monthlyRent), type: 'RENT', month: currentMonth, notes: '' });
    setShowPayment(t);
  };

  // ── Open edit modal ──────────────────────────────────────────────────────
  const openEdit = t => {
    setEditForm({
      roomId:        t.room.id,
      monthlyRent:   Number(t.monthlyRent),
      keyMoney:      Number(t.keyMoney),
      keyMoneyMonths: t.keyMoneyMonths || 2,
      startDate:     t.startDate ? new Date(t.startDate).toISOString().split('T')[0] : '',
    });
    setShowEdit(t);
  };

  const submitEdit = async e => {
    e.preventDefault();
    try {
      await api.put(`/tenancies/${showEdit.id}`, editForm);
      toast.success('Tenancy updated');
      setShowEdit(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const generateAgreement = async tenancyId => {
    try {
      const { data } = await api.post(`/agreements/tenancy/${tenancyId}/generate`);
      toast.success('Agreement generated — downloading...');
      const res = await api.get(`/agreements/download/${data.agreement.id}`, { responseType: 'blob' });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = href; a.download = `agreement-${tenancyId}.pdf`; a.click();
      URL.revokeObjectURL(href);
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const toggleExpand = id => setExpanded(p => ({ ...p, [id]: !p[id] }));

  // ── Shared card ──────────────────────────────────────────────────────────
  const TenancyCard = ({ t }) => {
    const isActive = t.status === 'ACTIVE';
    const isNotice = t.status === 'NOTICE_GIVEN';
    const isEnded = t.status === 'ENDED';
    const open = expanded[t.id];
    const keyMoneyPending = isEnded && !t.keyMoneyReturnDate;
    const deduction = isEnded && t.keyMoneyReturnDate
      ? Number(t.keyMoney) - Number(t.keyMoneyReturnAmount || 0)
      : null;

    return (
      <div className={`bg-white rounded-xl shadow-sm border transition-all
        ${isNotice ? 'border-amber-200' : isEnded ? 'border-gray-100' : 'border-gray-100'}`}>
        {/* Header row */}
        <div className="p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            {/* Left info */}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-gray-800">
                  {t.student.firstName} {t.student.lastName}
                </p>
                <span className="text-xs font-mono text-brand-600">{t.student.registrationNo}</span>
                {/* Status badge */}
                {isActive && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>}
                {isNotice && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex items-center gap-1">
                    <BellRing size={10} /> Notice Given
                  </span>
                )}
                {isEnded && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Ended</span>}
              </div>

              <p className="text-sm text-gray-500 mt-1">
                Room {t.room.roomNumber} · {fmtMoney(t.monthlyRent)}/mo
              </p>
              <p className="text-xs text-gray-400">Key Money: {fmtMoney(t.keyMoney)}</p>

              <div className="flex flex-wrap items-center gap-3 mt-1">
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Clock size={11} />
                  {isEnded
                    ? `${fmtDate(t.startDate)} – ${fmtDate(t.endDate)} · ${duration(t.startDate, t.endDate)}`
                    : `Since ${fmtDate(t.startDate)} · ${duration(t.startDate)}`}
                </span>
                {isNotice && t.exitDate && (
                  <span className="text-xs text-amber-600 flex items-center gap-1">
                    <CalendarDays size={11} /> Exit: {fmtDate(t.exitDate)}
                    <ExitCountdown exitDate={t.exitDate} />
                  </span>
                )}
              </div>

              {/* Key money return status for ended tenancies */}
              {isEnded && (
                <div className="mt-1.5">
                  {t.keyMoneyReturnDate ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                      <CheckCircle2 size={11} />
                      Key money returned {fmtDate(t.keyMoneyReturnDate)} · {fmtMoney(t.keyMoneyReturnAmount)}
                      {deduction > 0 && <span className="text-red-500 ml-1">(Rs. {deduction.toLocaleString()} deducted)</span>}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                      <KeyRound size={11} /> Key money pending return
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 items-start">
              {isActive && (
                <>
                  <button onClick={() => openPayment(t)}
                    className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full hover:bg-blue-100">
                    <DollarSign size={12} /> Payment
                  </button>
                  <button onClick={() => generateAgreement(t.id)}
                    className="flex items-center gap-1 text-xs bg-brand-50 text-brand-700 px-3 py-1.5 rounded-full hover:bg-brand-100">
                    <FileText size={12} /> Agreement
                  </button>
                  <button onClick={() => { setShowNotice(t); setNoticeForm({ noticeDate: new Date().toISOString().split('T')[0], exitDate: '' }); }}
                    className="flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-3 py-1.5 rounded-full hover:bg-amber-100">
                    <BellRing size={12} /> Give Notice
                  </button>
                  <button onClick={() => setConfirmEnd({ id: t.id, name: `${t.student.firstName} ${t.student.lastName}`, room: t.room.roomNumber })}
                    className="flex items-center gap-1 text-xs bg-red-50 text-red-700 px-3 py-1.5 rounded-full hover:bg-red-100">
                    <StopCircle size={12} /> End
                  </button>
                </>
              )}

              {isNotice && (
                <>
                  <button onClick={() => openPayment(t)}
                    className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full hover:bg-blue-100">
                    <DollarSign size={12} /> Payment
                  </button>
                  <button onClick={() => setConfirmExit(t)}
                    className="flex items-center gap-1 text-xs bg-red-50 text-red-700 px-3 py-1.5 rounded-full hover:bg-red-100">
                    <LogOut size={12} /> Process Exit
                  </button>
                </>
              )}

              {isEnded && keyMoneyPending && (
                <button onClick={() => {
                  setShowKeyReturn(t);
                  setKeyReturnForm({ returnDate: new Date().toISOString().split('T')[0], returnAmount: String(t.keyMoney), notes: '' });
                }}
                  className="flex items-center gap-1 text-xs bg-orange-50 text-orange-700 px-3 py-1.5 rounded-full hover:bg-orange-100">
                  <KeyRound size={12} /> Return Key Money
                </button>
              )}

              {/* Edit (active / notice_given) */}
              {(isActive || isNotice) && (
                <button
                  onClick={() => openEdit(t)}
                  title="Edit tenancy"
                  className="flex items-center gap-1 text-xs bg-gray-50 text-gray-400 hover:text-brand-600 hover:bg-brand-50 px-2 py-1.5 rounded-full transition-colors"
                >
                  <Pencil size={13} />
                </button>
              )}

              {/* Delete (all statuses) */}
              <button
                onClick={() => setConfirmDelete(t)}
                title="Delete this tenancy record"
                className="flex items-center gap-1 text-xs bg-red-50 text-red-400 hover:text-red-700 hover:bg-red-100 px-2 py-1.5 rounded-full transition-colors"
              >
                <Trash2 size={13} />
              </button>

              {/* Expand toggle */}
              <button onClick={() => toggleExpand(t.id)}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 px-2 py-1.5 rounded-full hover:bg-gray-50">
                {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
          </div>
        </div>

        {/* Expanded: recent payments + notice details */}
        {open && (
          <div className="border-t border-gray-50 px-4 pb-4 pt-3 space-y-3">
            {/* Notice details */}
            {isNotice && (
              <div className="bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-800 space-y-0.5">
                <p><span className="font-semibold">Notice given:</span> {fmtDate(t.noticeDate)}</p>
                <p><span className="font-semibold">Planned exit:</span> {fmtDate(t.exitDate)}</p>
              </div>
            )}

            {/* Key money return details for ended */}
            {isEnded && t.keyMoneyReturnDate && (
              <div className="bg-green-50 rounded-lg px-3 py-2 text-xs text-green-800 space-y-0.5">
                <p><span className="font-semibold">Key money returned:</span> {fmtDate(t.keyMoneyReturnDate)}</p>
                <p><span className="font-semibold">Amount:</span> {fmtMoney(t.keyMoneyReturnAmount)}
                  {deduction > 0 && <span className="text-red-600 ml-2">(Deducted Rs. {deduction.toLocaleString()})</span>}
                </p>
                {t.keyMoneyReturnNotes && <p><span className="font-semibold">Notes:</span> {t.keyMoneyReturnNotes}</p>}
              </div>
            )}

            {/* Payments */}
            {t.payments?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Recent Payments</p>
                <div className="flex flex-wrap gap-2">
                  {t.payments.map(p => (
                    <div key={p.id} className="text-xs bg-gray-50 border border-gray-100 rounded-lg px-2 py-1.5">
                      <span className="font-medium">{fmtMoney(p.amount)}</span>
                      <span className="text-gray-500 ml-1">· {p.type}{p.month && ` (${p.month})`} · {fmtDate(p.paymentDate)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!t.payments?.length && <p className="text-xs text-gray-400">No payments recorded yet</p>}
          </div>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-800">Tenancies</h2>
        <button onClick={() => setShowForm(true)}
          className="bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
          <Plus size={16} /> New Tenancy
        </button>
      </div>

      {/* Search bar */}
      <div className="relative mb-5">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, reg. no., room number or phone…"
          className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white shadow-sm"
        />
        {search && (
          <button onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs">
            ✕
          </button>
        )}
      </div>

      {/* Overdue exit alert */}
      {overdueExits.length > 0 && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-3">
          <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-700">
              {overdueExits.length} exit{overdueExits.length > 1 ? 's' : ''} overdue
            </p>
            <p className="text-xs text-red-600 mt-0.5">
              {overdueExits.map(t => `${t.student.firstName} ${t.student.lastName} (${fmtDate(t.exitDate)})`).join(', ')} —
              please process their exit.
            </p>
          </div>
          <button onClick={() => setTab('pending')} className="ml-auto text-xs text-red-700 underline whitespace-nowrap">
            View →
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-5 overflow-x-auto">
        <Tab active={tab === 'active'} onClick={() => setTab('active')} badge={active.length}>
          Active
        </Tab>
        <Tab active={tab === 'pending'} onClick={() => setTab('pending')} badge={pending.length}>
          Pending Exits
        </Tab>
        <Tab active={tab === 'ended'} onClick={() => setTab('ended')} badge={ended.length}>
          Ended
        </Tab>
      </div>

      {/* Tab content */}
      <div className="space-y-3">
        {tab === 'active' && (
          active.length === 0
            ? <Empty text="No active tenancies" />
            : active.map(t => <TenancyCard key={t.id} t={t} />)
        )}
        {tab === 'pending' && (
          pending.length === 0
            ? <Empty text="No pending exits — students with exit notices will appear here" />
            : pending.map(t => <TenancyCard key={t.id} t={t} />)
        )}
        {tab === 'ended' && (
          ended.length === 0
            ? <Empty text="No ended tenancies yet" />
            : ended.map(t => <TenancyCard key={t.id} t={t} />)
        )}
      </div>

      {/* ── New Tenancy Modal ────────────────────────────────────────────── */}
      {showForm && (
        <Modal title="New Tenancy" onClose={() => setShowForm(false)}>
          <form onSubmit={submit} className="space-y-3">
            <Field label="Student *">
              <select required value={form.studentId} onChange={e => setForm(p => ({ ...p, studentId: e.target.value }))}>
                <option value="">Select student</option>
                {students.filter(s => s.status === 'ACTIVE').map(s => (
                  <option key={s.id} value={s.id}>{s.firstName} {s.lastName} ({s.registrationNo})</option>
                ))}
              </select>
            </Field>
            <Field label="Room *">
              <select required value={form.roomId} onChange={e => {
                const r = rooms.find(r => r.id === Number(e.target.value));
                setForm(p => ({ ...p, roomId: e.target.value, monthlyRent: r?.monthlyRent || '' }));
              }}>
                <option value="">Select room</option>
                {rooms.map(r => (
                  <option key={r.id} value={r.id}>{r.roomNumber} — {r.category} ({r.status})</option>
                ))}
              </select>
            </Field>
            <Field label="Start Date *">
              <input type="date" required value={form.startDate} onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Monthly Rent *">
                <input type="number" required value={form.monthlyRent} onChange={e => setForm(p => ({ ...p, monthlyRent: e.target.value }))} />
              </Field>
              <Field label="Key Money *">
                <input type="number" required value={form.keyMoney} onChange={e => setForm(p => ({ ...p, keyMoney: e.target.value }))} />
              </Field>
            </div>
            <Field label="Key Money Months">
              <input type="number" min={1} value={form.keyMoneyMonths} onChange={e => setForm(p => ({ ...p, keyMoneyMonths: e.target.value }))} />
            </Field>
            <ModalButtons onCancel={() => setShowForm(false)} submitLabel="Create" />
          </form>
        </Modal>
      )}

      {/* ── Edit Tenancy Modal ──────────────────────────────────────────── */}
      {showEdit && (
        <Modal title="Edit Tenancy" onClose={() => setShowEdit(null)}>
          <p className="text-sm text-gray-500 -mt-2 mb-4">
            {showEdit.student.firstName} {showEdit.student.lastName} ·{' '}
            <span className="font-mono text-brand-600">{showEdit.student.registrationNo}</span>
          </p>
          <form onSubmit={submitEdit} className="space-y-3">
            <Field label="Room *">
              <select required value={editForm.roomId}
                onChange={e => setEditForm(p => ({ ...p, roomId: e.target.value }))}>
                <option value="">Select room</option>
                {rooms.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.roomNumber} — {r.category} ({r.status})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Start Date *">
              <input type="date" required value={editForm.startDate}
                onChange={e => setEditForm(p => ({ ...p, startDate: e.target.value }))} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Monthly Rent (Rs.) *">
                <input type="number" required value={editForm.monthlyRent}
                  onChange={e => setEditForm(p => ({ ...p, monthlyRent: e.target.value }))} />
              </Field>
              <Field label="Key Money (Rs.) *">
                <input type="number" required value={editForm.keyMoney}
                  onChange={e => setEditForm(p => ({ ...p, keyMoney: e.target.value }))} />
              </Field>
            </div>
            <Field label="Key Money Months">
              <input type="number" min={1} value={editForm.keyMoneyMonths}
                onChange={e => setEditForm(p => ({ ...p, keyMoneyMonths: e.target.value }))} />
            </Field>
            <ModalButtons onCancel={() => setShowEdit(null)} submitLabel="Save Changes" />
          </form>
        </Modal>
      )}

      {/* ── Add Payment Modal ────────────────────────────────────────────── */}
      {showPayment && (
        <Modal title="Record Payment" onClose={() => setShowPayment(null)} narrow>
          <form onSubmit={addPayment} className="space-y-3">
            <Field label="Amount (Rs.) *">
              <input type="number" required value={payForm.amount} onChange={e => setPayForm(p => ({ ...p, amount: e.target.value }))} />
            </Field>
            <Field label="Type *">
              <select value={payForm.type} onChange={e => setPayForm(p => ({ ...p, type: e.target.value }))}>
                <option value="RENT">Rent</option>
                <option value="KEY_MONEY">Key Money</option>
                <option value="OTHER">Other</option>
              </select>
            </Field>
            <Field label="Month (e.g. 2024-01)">
              <input type="month" value={payForm.month} onChange={e => setPayForm(p => ({ ...p, month: e.target.value }))} />
            </Field>
            <Field label="Notes">
              <input value={payForm.notes} onChange={e => setPayForm(p => ({ ...p, notes: e.target.value }))} />
            </Field>
            <ModalButtons onCancel={() => setShowPayment(null)} submitLabel="Save Payment" />
          </form>
        </Modal>
      )}

      {/* ── Give Notice Modal ────────────────────────────────────────────── */}
      {showNotice && (
        <Modal title={`Exit Notice — ${showNotice.student.firstName} ${showNotice.student.lastName}`} onClose={() => setShowNotice(null)} narrow>
          <p className="text-sm text-gray-500 mb-4">
            Record the exit notice and agreed move-out date.
            The tenancy will move to <strong>Pending Exits</strong> and the room space will be flagged as opening soon.
          </p>
          <form onSubmit={submitNotice} className="space-y-3">
            <Field label="Notice Date *">
              <input type="date" required value={noticeForm.noticeDate}
                onChange={e => setNoticeForm(p => ({ ...p, noticeDate: e.target.value }))} />
            </Field>
            <Field label="Agreed Exit Date *">
              <input type="date" required value={noticeForm.exitDate}
                onChange={e => setNoticeForm(p => ({ ...p, exitDate: e.target.value }))} />
            </Field>
            <div className="bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-700">
              ℹ️ The student will remain in Room {showNotice.room.roomNumber} until this exit date.
              You can still record payments and process the exit manually when she leaves.
            </div>
            <ModalButtons onCancel={() => setShowNotice(null)} submitLabel="Record Notice" submitColor="amber" />
          </form>
        </Modal>
      )}

      {/* ── Return Key Money Modal ───────────────────────────────────────── */}
      {showKeyReturn && (
        <Modal
          title={`Return Key Money — ${showKeyReturn.student.firstName} ${showKeyReturn.student.lastName}`}
          onClose={() => setShowKeyReturn(null)}
          narrow
        >
          <div className="bg-gray-50 rounded-lg px-3 py-2 text-sm mb-4 space-y-1">
            <p className="text-gray-600">Original key money: <strong>{fmtMoney(showKeyReturn.keyMoney)}</strong></p>
            <p className="text-xs text-gray-400">You can return a lesser amount if deductions apply.</p>
          </div>
          <form onSubmit={submitKeyReturn} className="space-y-3">
            <Field label="Return Date *">
              <input type="date" required value={keyReturnForm.returnDate}
                onChange={e => setKeyReturnForm(p => ({ ...p, returnDate: e.target.value }))} />
            </Field>
            <Field label="Amount Returned (Rs.) *">
              <input type="number" required value={keyReturnForm.returnAmount}
                onChange={e => setKeyReturnForm(p => ({ ...p, returnAmount: e.target.value }))} />
            </Field>
            {keyReturnForm.returnAmount && Number(keyReturnForm.returnAmount) < Number(showKeyReturn.keyMoney) && (
              <div className="bg-red-50 rounded-lg px-3 py-2 text-xs text-red-700">
                Deduction: Rs. {(Number(showKeyReturn.keyMoney) - Number(keyReturnForm.returnAmount)).toLocaleString()}
              </div>
            )}
            <Field label="Reason / Notes (optional)">
              <input placeholder="e.g. damage deduction Rs. 2,000" value={keyReturnForm.notes}
                onChange={e => setKeyReturnForm(p => ({ ...p, notes: e.target.value }))} />
            </Field>
            <ModalButtons onCancel={() => setShowKeyReturn(null)} submitLabel="Confirm Return" submitColor="green" />
          </form>
        </Modal>
      )}

      {/* ── Confirm Process Exit ─────────────────────────────────────────── */}
      <ConfirmDialog
        open={!!confirmExit}
        title="Process Student Exit"
        message={confirmExit
          ? `Confirm that ${confirmExit.student.firstName} ${confirmExit.student.lastName} has moved out of Room ${confirmExit.room.roomNumber}. The tenancy will be ended and the room space freed.`
          : ''}
        confirmLabel="Yes, Process Exit"
        onConfirm={doProcessExit}
        onCancel={() => setConfirmExit(null)}
      />

      {/* ── Legacy confirm end (from active card End button) ────────────── */}
      <ConfirmDialog
        open={!!confirmEnd}
        title="End Tenancy"
        message={confirmEnd
          ? `End tenancy for ${confirmEnd.name} in Room ${confirmEnd.room}? This marks the room as available immediately.`
          : ''}
        confirmLabel="Yes, End Tenancy"
        onConfirm={async () => {
          try {
            await api.put(`/tenancies/${confirmEnd.id}/end`);
            toast.success('Tenancy ended');
            setConfirmEnd(null);
            load();
          } catch (err) { toast.error(err.response?.data?.error || 'Error'); setConfirmEnd(null); }
        }}
        onCancel={() => setConfirmEnd(null)}
      />

      {/* ── Confirm Delete ───────────────────────────────────────────────── */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 size={18} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Delete Tenancy?</h3>
                <p className="text-sm text-gray-500 mt-1">
                  This will permanently delete the tenancy for{' '}
                  <strong>{confirmDelete.student.firstName} {confirmDelete.student.lastName}</strong>{' '}
                  in Room <strong>{confirmDelete.room.roomNumber}</strong>.
                </p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-xs text-red-700 mb-4">
              ⚠️ All payment records and agreements linked to this tenancy will also be deleted. This cannot be undone.
            </div>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={doDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg text-sm font-medium">
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────
function Empty({ text }) {
  return <div className="text-center text-gray-400 py-16 text-sm">{text}</div>;
}

function Modal({ title, onClose, children, narrow = false }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-xl p-6 w-full shadow-xl ${narrow ? 'max-w-sm' : 'max-w-md'}`}>
        <h3 className="font-bold text-lg mb-4 text-gray-800">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  const child = React.cloneElement(React.Children.only(children), {
    className: 'w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500',
  });
  return (
    <div>
      <label className="text-sm font-medium text-gray-700">{label}</label>
      {child}
    </div>
  );
}

function ModalButtons({ onCancel, submitLabel, submitColor = 'brand' }) {
  const colors = {
    brand: 'bg-brand-700 hover:bg-brand-800 text-white',
    amber: 'bg-amber-500 hover:bg-amber-600 text-white',
    green: 'bg-green-600 hover:bg-green-700 text-white',
  };
  return (
    <div className="flex gap-3 pt-2">
      <button type="button" onClick={onCancel}
        className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">
        Cancel
      </button>
      <button type="submit" className={`flex-1 py-2 rounded-lg text-sm font-medium ${colors[submitColor]}`}>
        {submitLabel}
      </button>
    </div>
  );
}
